import React from 'react';
import { motion } from 'framer-motion';
import { Wind, Battery, BrainCircuit, AlertTriangle, ArrowRight, CheckCircle2, ShieldAlert, Heart } from 'lucide-react';
const problems = ['Cambias de marca cada mes sin ver mejoras', 'Agregas toppers "porque sí" sin medir', 'Das snacks al azar y no sabes si son muchos', 'No tienes idea de cuánto debería comer realmente', 'No sabes qué comidas le caen mal a tu Frenchie'];
const benefits = [{
  icon: Wind,
  title: 'Menos gases',
  description: 'Reduce variables y estabiliza su digestión desde el día 1. Sin más olores incómodos.',
  color: 'from-emerald-400 to-emerald-600',
  bgColor: 'bg-emerald-50',
  borderColor: 'border-emerald-200',
  iconBg: 'bg-emerald-500'
}, {
  icon: Battery,
  title: 'Energía más estable',
  description: 'Rutina clara con bowls repetibles que mantienen su energía constante todo el día.',
  color: 'from-amber-400 to-orange-500',
  bgColor: 'bg-amber-50',
  borderColor: 'border-amber-200',
  iconBg: 'bg-amber-500'
}, {
  icon: BrainCircuit,
  title: 'Menos estrés para ti',
  description: 'Meal prep simple de 20 min semanales. Porcionado listo. Sin improvisar cada día.',
  color: 'from-violet-400 to-purple-600',
  bgColor: 'bg-violet-50',
  borderColor: 'border-violet-200',
  iconBg: 'bg-violet-500'
}, {
  icon: ShieldAlert,
  title: 'Reduce alergias',
  description: 'Identifica ingredientes problemáticos y crea un plan seguro para tu Frenchie sensible.',
  color: 'from-rose-400 to-pink-600',
  bgColor: 'bg-rose-50',
  borderColor: 'border-rose-200',
  iconBg: 'bg-rose-500'
}, {
  icon: Heart,
  title: 'Mejora la salud',
  description: 'Nutrición balanceada que fortalece su sistema inmune, piel brillante y vitalidad duradera.',
  color: 'from-pink-400 to-red-500',
  bgColor: 'bg-pink-50',
  borderColor: 'border-pink-200',
  iconBg: 'bg-pink-500'
}];
export function ProblemSection() {
  return <section className="py-20 bg-gradient-to-b from-white via-[#FDF4FF]/30 to-white relative overflow-hidden" data-id="element-205">
      {/* Background decorations */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-red-100/40 rounded-full blur-3xl" data-id="element-206" />
      <div className="absolute bottom-20 right-0 w-80 h-80 bg-emerald-100/40 rounded-full blur-3xl" data-id="element-207" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-208">
        {/* Problem Statement */}
        <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-209">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-100 text-red-600 text-sm font-semibold mb-6" data-id="element-210">
            <AlertTriangle className="w-4 h-4" data-id="element-211" />
            ¿Te suena familiar?
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold text-[#2D2D2D] mb-6 leading-tight" data-id="element-212">
            El problema <span className="text-red-500" data-id="element-213">no es la croqueta.</span>
            <br data-id="element-214" />
            <span className="bg-gradient-to-r from-[#D946EF] to-[#A21CAF] bg-clip-text text-transparent" data-id="element-215">
              Es la falta de estructura.
            </span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-[#666666] leading-relaxed max-w-2xl mx-auto" data-id="element-216">
            Cuando todo es improvisado, la alimentación de tu Frenchie se vuelve
            una lotería.
          </motion.p>
        </div>

        {/* Problems List */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto mb-16" data-id="element-217">
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 md:p-8 border-2 border-red-100 shadow-lg" data-id="element-218">
            <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center gap-2" data-id="element-219">
              <AlertTriangle className="w-5 h-5" data-id="element-220" />
              Señales de que necesitas este sistema:
            </h3>
            <ul className="space-y-3" data-id="element-221">
              {problems.map((problem, index) => <motion.li key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="flex items-start gap-3 text-[#4A4A4A]" data-id="element-222">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center mt-0.5" data-id="element-223">
                    ✗
                  </span>
                  <span className="text-base" data-id="element-224">{problem}</span>
                </motion.li>)}
            </ul>
          </div>
        </motion.div>

        {/* Transition Arrow */}
        <motion.div initial={{
        opacity: 0,
        scale: 0
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="flex justify-center mb-12" data-id="element-225">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D946EF] to-[#A21CAF] flex items-center justify-center shadow-lg shadow-[#D946EF]/30" data-id="element-226">
            <ArrowRight className="w-8 h-8 text-white rotate-90" data-id="element-227" />
          </div>
        </motion.div>

        {/* Solution Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-228">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-600 text-sm font-semibold mb-4" data-id="element-229">
            <CheckCircle2 className="w-4 h-4" data-id="element-230" />
            La solución
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-[#2D2D2D]" data-id="element-231">
            Con Bowl Upgrade obtienes:
          </h3>
        </motion.div>

        {/* Benefits Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-232">
          {benefits.map((benefit, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.15
        }} className={`${benefit.bgColor} p-8 rounded-3xl border-2 ${benefit.borderColor} shadow-lg relative overflow-hidden group`} data-id="element-233">
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${benefit.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} data-id="element-234" />

              <div className={`w-14 h-14 ${benefit.iconBg} rounded-2xl flex items-center justify-center mb-6 shadow-lg transform group-hover:scale-110 transition-transform duration-300`} data-id="element-235">
                <benefit.icon className="w-7 h-7 text-white" data-id="element-236" />
              </div>

              <h3 className="text-2xl font-bold text-[#2D2D2D] mb-3" data-id="element-237">
                {benefit.title}
              </h3>

              <p className="text-[#4A4A4A] leading-relaxed" data-id="element-238">
                {benefit.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}